import {
  buildUseMutation,
  ErrorText,
  Form,
  formatErrorMessage,
  message,
  SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY,
} from '@main/core-ui';
import type { DataSubject } from '@main/datamap-types';
import { ImmutableUrl } from '@main/immutable-url';
import { endpoints, PAGE_PATHNAMES } from '@main/pc-types';
import {
  createDhEncryptionKeys,
  createDhPayloadHelpers,
} from '@main/sm-browser';
import { SombraDataSubjectAuthMethod } from '@main/sombra-types';
import { LanguageKey } from '@transcend-io/internationalization';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { selectChildSessionProfiles } from '../../containers/App/selectors';
import { ChildOrgWithPurposes } from '../../hooks';
import {
  getSetAllPreferencesInput,
  getUpdateDataSubjectConsentPreferencesInput,
  transformPurposesToFormPurposes,
} from '../helpers';
import {
  ChildOrganizationHeader,
  Purpose,
  PurposeListForm,
} from './components';
import { communicationsPreferencesMessages } from './messages';
import { OrganizationFormController } from './types';

interface ChildOrganizationPreferencesFormProps {
  /** Child organization */
  childOrganization: ChildOrgWithPurposes;
  /** Whether the Privacy Center is in Draft mode  */
  isDraft: boolean;
  /** The currently selected data subject */
  activeDataSubject?: DataSubject;
  /** Core Identifier who's preferences are being updated */
  coreIdentifier?: string;
  /** Function to add form state/actions to the parent component */
  registerAction: (val: OrganizationFormController) => void;
  /** Function to remove form state/actions to parent component */
  unregisterAction: (id: string) => void;
}

const useDataSubjectUpdateConsentPreferences = buildUseMutation(
  endpoints.dataSubjectUpdateConsentPreferences,
);

export const ChildOrganizationPreferencesForm: React.FC<
  ChildOrganizationPreferencesFormProps
> = ({
  childOrganization,
  activeDataSubject,
  registerAction,
  unregisterAction,
  coreIdentifier,
  isDraft,
}) => {
  const { locale, formatMessage } = useIntl();

  const childSessionProfiles = useSelector(selectChildSessionProfiles);

  const [authError, setAuthError] = useState<string | null>(null);

  // API call to update preferences
  const [updateDataSubjectConsentPreferences, { error }] =
    useDataSubjectUpdateConsentPreferences({
      refetchQueries: [
        'DataSubjectConsentPreferences',
        'RequestHistory',
        'RequestHistoryActiveCount',
      ],
      awaitRefetchQueries: true,
    });

  // Purposes configured for this child org to display in the privacy center
  const configuredCommunicationPurposes = useMemo(
    () =>
      childOrganization.purposes!.filter(
        ({ showInPrivacyCenter }) => showInPrivacyCenter,
      ),
    [childOrganization],
  );

  // Privacy center URL: On draft mode, skip privacy center modules
  const privacyCenterUrl = useMemo(() => {
    const url = new ImmutableUrl(
      childOrganization.deployedPrivacyCenterUrl,
    ).transform({
      pathname: PAGE_PATHNAMES.COMMUNICATIONS_PREFERENCES,
      ...(isDraft
        ? {
            searchTransform: (qs) => ({
              ...qs,
              [SKIP_PRIVACY_CENTER_MODULES_FLAG_KEY]: true,
            }),
          }
        : {}),
    }).href;
    return url;
  }, [childOrganization.deployedPrivacyCenterUrl, isDraft]);

  // Purposes configured for this child org to display in the privacy center
  const defaultFormPurposes = useMemo(
    () =>
      transformPurposesToFormPurposes(
        childOrganization.consentPreferences ?? [],
        configuredCommunicationPurposes,
      ),
    [childOrganization.consentPreferences, configuredCommunicationPurposes],
  );

  /**
   * On form submit, updates communication preferences
   *
   * @param data - form data
   */
  const handleSubmit = async (data: {
    /** email address */
    email: string;
    /** consent selections */
    purposes: Record<string, Purpose>;
  }): Promise<void> => {
    if (
      activeDataSubject &&
      coreIdentifier &&
      updateDataSubjectConsentPreferences
    ) {
      const { input: updatedRow, dhEncryptedInput } =
        getUpdateDataSubjectConsentPreferencesInput(
          data.email,
          childOrganization.identifiers ?? [],
          childOrganization.airgapBundleId!,
          data.purposes,
        );

      const childSessionProfile = childSessionProfiles?.find(
        ({ organizationId }) => organizationId === childOrganization.id,
      );

      if (!childSessionProfile) {
        setAuthError(
          `Missing auth secret for child organization: ${childOrganization.name}`,
        );
        return;
      }

      const [createDhPayload] = createDhPayloadHelpers({
        authMethod: SombraDataSubjectAuthMethod.Session,
        authSecret: childSessionProfile.authSecret,
        dhEncryptionKeys: createDhEncryptionKeys(
          childOrganization.sombraPublicKeyECDH!,
        ),
      });

      if (createDhPayload) {
        await updateDataSubjectConsentPreferences({
          variables: {
            input: {
              consentPreferences: [updatedRow],
              locale: locale as LanguageKey,
              subjectType: activeDataSubject.type,
            },
            lookup: { url: childOrganization.deployedPrivacyCenterUrl },
            dhEncrypted: createDhPayload({
              ...dhEncryptedInput,
            }),
          },
        }).then(({ data: updatedResponse }) => {
          if (updatedResponse) {
            message.success(
              formatMessage(communicationsPreferencesMessages.success),
            );
          }
        });
      }
    }
  };

  const formMethods = useForm({
    shouldUnregister: false,
    defaultValues: {
      email: coreIdentifier ?? '',
      purposes: defaultFormPurposes,
    },
  });

  // Pass form action/state to parent organization's component
  useEffect(() => {
    registerAction({
      id: childOrganization.id,
      isDirty: formMethods.formState.isDirty,
      setAllValues: (val) => {
        const latestConsent = getSetAllPreferencesInput(
          val,
          configuredCommunicationPurposes,
        );
        formMethods.setValue('purposes', latestConsent, { shouldDirty: true });
      },
      resetValues: () => {
        formMethods.reset({ purposes: defaultFormPurposes });
      },
      submitForm: () => {
        formMethods.handleSubmit((data) => {
          handleSubmit(data);
        })();
      },
    });
    return () => unregisterAction(childOrganization.id);
  }, [childOrganization, formMethods.formState.isDirty]);

  // Effect to show error message if any
  useEffect(() => {
    if (error) {
      message.error(formatErrorMessage(error.message));
    }
  }, [error]);

  return (
    <Form
      useFormMethods={formMethods}
      onSubmit={(data) => {
        handleSubmit(data);
      }}
    >
      <ChildOrganizationHeader
        logo={childOrganization.logo}
        name={childOrganization.name}
        url={privacyCenterUrl}
      />
      <div style={{ padding: '15px' }}>
        {childOrganization.failedToLoad || authError ? (
          <ErrorText>
            {authError ||
              formatMessage(
                communicationsPreferencesMessages.failedToLoadPurposes,
              )}
          </ErrorText>
        ) : (
          <PurposeListForm
            hideIfNotConsented
            name="purposes"
            purposes={configuredCommunicationPurposes}
          />
        )}
      </div>
    </Form>
  );
};
